// src/therapist/test-therapist/TestDashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, Tooltip, ResponsiveContainer } from 'recharts';
import { motion, AnimatePresence } from "framer-motion";
// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35 } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.35 } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.4 } },
};


import {
  Grid,
  MessageSquare,
  FileText,
  User,
  Clock,
  Calendar as CalendarIcon,
  ClipboardListIcon,
  MenuIcon,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  Phone,
  Video,
  Users as UsersIcon,
  FileText as FileTextIcon,
  Briefcase,
  Star as StarIcon,
  Search as SearchIcon,
} from 'lucide-react';

import DirectMessages from '../../common/DirectMessages';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DashboardCalendar from './DashboardCalendar';
import { useAuth } from '../../lib/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { parseISO, isToday, isTomorrow, format } from 'date-fns';
import { Button } from '../../components/ui/button';
import Notifications from '../../components/Notifications';
import ExpertProfile from '../../expert/ExpertProfile';
import Notes from '../../Notes';
import Availability from '../../expert/Availability';
import ExpertBookings from '../../expert/ExpertBookings';
import ExpertTestimonials from '../../expert/ExpertTestimonials';
import ExpertServices from '../../expert/ExpertServices';
import ExpertAnalytics from '../../expert/ExpertAnalytics';
import ExpertMarketing from '../../expert/ExpertMarketing';
import ExpertPayments from '../../expert/ExpertPayments';
import ExpertCheckout from '../../expert/ExpertCheckout';
import ExpertAchievements from '../../expert/ExpertAchievements';
import ExpertResources from '../../expert/ExpertResources';

const SidebarItem = ({ icon: Icon, label, to, activeContent, setActiveContent, isSidebarExpanded }) => (
  <button
    onClick={() => setActiveContent(to)}
    className={`flex items-center p-2 rounded-lg w-full text-sm transition-colors ${activeContent === to ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-50'
      }`}
  >
    <Icon className="h-5 w-5" />
    {isSidebarExpanded && <span className="ml-3">{label}</span>}
  </button>
);

const TestDashboard = () => {
  const { accessToken, user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [importantBooking, setImportantBooking] = useState(null);
  const [bookingVersion, setBookingVersion] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isOnline, setIsOnline] = useState(user?.is_online || false);
  const [activeContent, setActiveContent] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tips, setTips] = useState([]);
  const [quote, setQuote] = useState({ text: '', author: '' });

  useEffect(() => {
    if (user) setIsOnline(user.is_online);
  }, [user]);

  useEffect(() => {
    console.log('%c🔥 TestDashboard Therapist is Mounted', 'background:#0ea5a4;color:white;padding:6px');
  }, []);

  const toggleProfileModal = () => setShowProfileModal(p => !p);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`https://quirkleai.com/api/bookings/?type=1on1&status=upcoming`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const now = new Date();
      const formatted = res.data
        .map(b => {
          const start = parseISO(b.start_time);
          const end = parseISO(b.end_time);
          return { ...b, isLive: now >= start && now <= end };
        })
        .sort((a, b) => parseISO(a.start_time) - parseISO(b.start_time));

      setImportantBooking(formatted[0] || null);
      setBookings(formatted.slice(0, 3));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load bookings.');
    }
  }, [accessToken]);

  useEffect(() => {
    fetchBookings();
    axios.get('https://quirkleai.com/api/wellness-tips/').then(res => setTips(res.data)).catch(() => { });
    axios.get('https://quirkleai.com/api/motivation/').then(res => setQuote(res.data)).catch(() => { });
  }, [fetchBookings, bookingVersion]);

  const handleStartNow = async (sessionId, meetingId) => {
    try {
      await axios.patch(`https://quirkleai.com/api/sessions/${sessionId}/activate/`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Meeting activated!');
      navigate(`/therapist/jitsi-room/${meetingId}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to activate meeting.');
    }
  };

  const handleDelete = async (sessionId) => {
    try {
      await axios.delete(`https://quirkleai.com/api/bookings/${sessionId}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Booking deleted.');
      setBookingVersion(v => v + 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete booking.');
    }
  };

  const handleOnlineToggle = async () => {
    try {
      const res = await fetch(`https://quirkleai.com/api/me/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ is_online: !isOnline }),
      });
      if (!res.ok) throw new Error('Failed');
      const updated = await res.json();
      setIsOnline(updated.is_online);
      setUser(updated);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update online status.');
    }
  };

  const getRelativeTimeInfo = (booking) => {
    if (!booking) return '';
    const start = parseISO(booking.start_time);
    if (booking.isLive) return 'Now';
    if (isToday(start)) return 'Today';
    if (isTomorrow(start)) return 'Tomorrow';
    return format(start, 'MMM d');
  };

  const monthlyVisitorsData = [
    { name: 'Jun', value: 5 },
    { name: 'Feb', value: 8 },
    { name: 'Mar', value: 6 },
    { name: 'Apr', value: 10 },
  ];

  const sidebarItems = [
    { name: 'dashboard', icon: Grid, label: 'Dashboard' },
    { name: 'priorityDM', icon: MessageSquare, label: 'Priority DM' },
    { name: 'notes', icon: FileText, label: 'Notes' },
    { name: 'profile', icon: User, label: 'Profile' },
    { name: 'testimonials', icon: StarIcon, label: 'Testimonials' },
    { name: 'availability', icon: Clock, label: 'Availability' },
    { name: 'calendar', icon: CalendarIcon, label: 'Calendar' },
    { name: 'bookings', icon: ClipboardListIcon, label: 'Bookings' },
    { name: 'services', icon: FileTextIcon, label: 'Services' },
    { name: 'analytics', icon: FileTextIcon, label: 'Analytics' },
    { name: 'marketing', icon: Phone, label: 'Marketing' },
    { name: 'payments', icon: FileTextIcon, label: 'Payments' },
    { name: 'checkout', icon: FileTextIcon, label: 'Checkout' },
    { name: 'achievements', icon: StarIcon, label: 'Achievements' },
    { name: 'resources', icon: FileTextIcon, label: 'Resources' },
  ];

  const visibleItems = showMore ? sidebarItems : sidebarItems.slice(0, 7);

  return (
    <div className="flex h-screen bg-[#F7F8FA]">

      {/* DESKTOP SIDEBAR (hidden on small screens) */}
      <motion.aside
        initial={false}
        animate={{ width: isSidebarExpanded ? 260 : 80 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="hidden md:flex flex-col bg-white py-4 shadow-md h-screen"
      >
        <div className="flex justify-start px-2 mb-4">
          <button
            onClick={() => setIsSidebarExpanded((s) => !s)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 space-y-2">
          {visibleItems.map((item) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
            >
              <SidebarItem
                icon={item.icon}
                label={item.label}
                to={item.name}
                activeContent={activeContent}
                setActiveContent={setActiveContent}
                isSidebarExpanded={isSidebarExpanded}
              />
            </motion.div>
          ))}

          {sidebarItems.length > 7 && (
            <motion.button
              onClick={() => setShowMore((p) => !p)}
              whileHover={{ x: 4 }}
              className="flex items-center p-2 rounded-lg w-full text-sm text-gray-600 hover:bg-gray-50"
            >
              {showMore ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              {isSidebarExpanded && (
                <span className="ml-3">{showMore ? "Show Less" : "Show More"}</span>
              )}
            </motion.button>
          )}
        </div>

        <div className="mt-auto px-2 pb-4">
          <button
            onClick={logout}
            className="flex items-center p-2 text-gray-600 hover:bg-gray-50 rounded-lg w-full"
          >
            🚪 {isSidebarExpanded && "Logout"}
          </button>
        </div>
      </motion.aside>


      {/* MAIN */}
      <main className={`flex-1 grid grid-cols-12 gap-6 p-4 md:p-6 overflow-y-auto transition-all duration-300 ${isSidebarExpanded ? 'md:ml-72' : 'md:ml-20'}`}>
        {/* HEADER */}
        <div className="col-span-12">
          <motion.header
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >

            {/* left: hamburger on mobile + title */}
            <div className="flex items-center gap-6">
              <button
                className="md:hidden p-2 rounded-lg bg-white shadow-sm"
                onClick={() => setMobileMenuOpen(prev => !prev)}
                aria-label="Open menu"
              >
                <MenuIcon className="w-5 h-5 text-gray-700" />
              </button>

              <div>
                <h1 className="text-3xl font-bold text-gray-800">Overview</h1>
                <p className="text-gray-500">Today is a great day to serve our patients.</p>
              </div>
            </div>

            {/* right: search + notifications (professional medium gap) */}
            <div className="flex items-center gap-6">
              {/* Desktop search */}
              <div className="relative hidden sm:block">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 rounded-lg bg-white border focus:ring-2 focus:ring-green-400 w-56"
                />
              </div>

              {/* Mobile search component */}
              <MobileSearch />

              {/* Notifications */}
              <Notifications accessToken={accessToken} />

              {/* Online toggle (desktop pill) */}
              <div
                onClick={handleOnlineToggle}
                className={`hidden sm:flex items-center rounded-full px-4 py-2 transition-all duration-300 text-white font-semibold cursor-pointer ${isOnline ? 'bg-green-500' : 'bg-red-500'
                  }`}
                role="button"
                aria-pressed={isOnline}
              >
                <span>{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </motion.header>

          {/* MOBILE SIDEBAR (GMAIL STYLE) */}
          <div className="md:hidden">
            <AnimatePresence>
              {mobileMenuOpen && (
                <>
                  {/* Backdrop */}
                  <motion.div
                    className="fixed inset-0 bg-black/40 z-40"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setMobileMenuOpen(false)}
                  />

                  {/* Slide-in panel */}
                  <motion.div
                    className="fixed left-0 top-0 h-full w-72 bg-white shadow-xl z-50 p-4 flex flex-col"
                    initial={{ x: -260 }}
                    animate={{ x: 0 }}
                    exit={{ x: -260 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    {/* Top row */}
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold">Menu</h2>
                      <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-gray-700">
                        ✖
                      </button>
                    </div>

                    {/* Navigation items */}
                    <motion.nav
                      initial="hidden"
                      animate="show"
                      variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.12 } },
                      }}
                      className="flex-1 overflow-y-auto"
                    >
                      {sidebarItems.map((item) => (
                        <motion.button
                          key={item.name}
                          onClick={() => {
                            setActiveContent(item.name);
                            setMobileMenuOpen(false);
                          }}
                          variants={{
                            hidden: { opacity: 0, x: -20 },
                            show: { opacity: 1, x: 0 },
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg"
                        >
                          <item.icon className="w-5 h-5" />
                          <span>{item.label}</span>
                        </motion.button>
                      ))}
                    </motion.nav>

                    {/* Online toggle */}
                    <motion.div variants={fadeUp} className="px-4 py-3 border-t">
                      <button
                        onClick={handleOnlineToggle}
                        className={`w-full flex items-center justify-center rounded-lg py-2 font-semibold text-white ${isOnline ? "bg-green-500" : "bg-red-500"
                          }`}
                      >
                        {isOnline ? "You are Online" : "You are Offline"}
                      </button>
                    </motion.div>

                    {/* Logout */}
                    <motion.button
                      variants={fadeUp}
                      onClick={logout}
                      className="w-full text-left px-4 py-3 text-red-600"
                    >
                      🚪 Logout
                    </motion.button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

          </div>
        </div>

        {/* ---------------- MAIN CONTENT VIEWS ---------------- */}
        {activeContent === 'dashboard' && (
          <>
            {/* LEFT COLUMN */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              {/* Upcoming Appointments */}
              <Card className="shadow-sm rounded-xl">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Upcoming Appointments</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="text-left text-gray-400 text-sm">
                          <th className="pb-3">Name</th>
                          <th className="pb-3">Disease</th>
                          <th className="pb-3">Date</th>
                          <th className="pb-3">Time</th>
                          <th className="pb-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map(b => (
                          <tr key={b.id} className="border-t border-gray-100">
                            <td className="py-3 flex items-center">
                              <img src={`https://i.pravatar.cc/40?u=${b.client.username}`} alt={b.client.username} className="rounded-full mr-3" />
                              <div><p className="font-semibold text-gray-700">{b.client.username}</p></div>
                            </td>
                            <td className="py-3 text-gray-600">N/A</td>
                            <td className="py-3 text-gray-600">{new Date(b.start_time).toLocaleDateString()}</td>
                            <td className="py-3 text-gray-600">{new Date(b.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="py-3">
                              {b.isLive ? (
                                <Button className="bg-green-500 text-white hover:bg-green-600 px-4 py-2 rounded-lg" onClick={() => handleStartNow(b.id, b.meeting_id)}>Start Now</Button>
                              ) : (
                                <Button className="bg-red-500 text-white hover:bg-red-600 px-4 py-2 rounded-lg" onClick={() => handleDelete(b.id)}>Delete</Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Calendar — responsive wrapper */}
              <Card className="shadow-sm rounded-xl">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Your Schedule</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="w-full overflow-x-auto">
                    {/* DashboardCalendar itself will render FullCalendar; keeping wrapper fluid so it can shrink on small screens */}
                    <DashboardCalendar bookingVersion={bookingVersion} onBookingAdded={() => setBookingVersion(v => v + 1)} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* RIGHT COLUMN */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              <Card className="bg-[#4E4E8D] text-white rounded-xl shadow-lg">
                <CardHeader className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <img src={user?.profile_picture ? `http://localhost:8000${user.profile_picture}` : 'https://i.pravatar.cc/40?u=random'} alt="profile" className="rounded-full border-2 border-white" style={{ width: 40, height: 40 }} />
                    <div>
                      <CardTitle className="text-base font-bold">{user?.first_name} {user?.last_name}</CardTitle>
                      <p className="text-xs opacity-80">{user?.headline || 'Psychology'}</p>
                    </div>
                  </div>
                  <MoreHorizontal className="cursor-pointer" onClick={() => setShowProfileModal(true)} />
                </CardHeader>

                <CardContent>
                  <div className="bg-[#6A6AB4] p-4 rounded-lg">
                    <p className="text-sm font-semibold">Specialist</p>
                    <p className="text-xl font-bold">Mental Health</p>
                    <div className="flex justify-between mt-3 text-sm">
                      <div>
                        <p className="opacity-80">Next appointment</p>
                        <p className="font-semibold">N/A</p>
                      </div>
                      <div>
                        <p className="opacity-80">Time</p>
                        <p className="font-semibold">N/A</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex mt-4 space-x-2">
                    <button className="flex-1 bg-white text-[#4E4E8D] font-semibold py-2.5 rounded-lg" onClick={() => setActiveContent('bookings')}>Bookings</button>
                    <button className="p-2.5 bg-white/20 rounded-lg"><Phone className="w-5 h-5" /></button>
                    <button className="p-2.5 bg-white/20 rounded-lg"><Video className="w-5 h-5" /></button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4">
                <StatCard value="212" label="Patients" icon={<UsersIcon className="text-red-500" />} />
                <StatCard value="114" label="Reports" icon={<FileTextIcon className="text-blue-500" />} />
                <StatCard value="182" label="Consultations" icon={<Briefcase className="text-yellow-500" />} />
                <StatCard value="127" label="Experience" icon={<StarIcon className="text-green-500" />} />
              </div>

              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Your Activity</CardTitle>
                  <button className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-md">1 Week <ChevronDown className="w-4 h-4 ml-1" /></button>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={monthlyVisitorsData}>
                      <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
                      <Tooltip />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Important Schedule</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>
                <CardContent>
                  {importantBooking ? (
                    <>
                      <div className="flex items-center gap-3">
                        <img src={`https://i.pravatar.cc/40?u=${importantBooking.client.username}`} className="rounded-full" />
                        <div>
                          <p className="font-semibold text-gray-700">Session with {importantBooking.client.username}</p>
                          <p className="text-sm text-gray-500">{getRelativeTimeInfo(importantBooking)} | {format(parseISO(importantBooking.start_time), 'p')}</p>
                        </div>
                      </div>
                      <button onClick={() => importantBooking.isLive ? handleStartNow(importantBooking.id, importantBooking.meeting_id) : toast.info('You can only start at scheduled time.')} className="w-full bg-green-500 text-white rounded-lg font-semibold py-2.5 mt-4">Start Call</button>
                    </>
                  ) : (
                    <p className="text-gray-500 text-center">No upcoming appointments.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Latest Notification</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-gray-700">Webinar available</p>
                  <p className="text-sm text-gray-500">Check the schedule for details.</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* OTHER TABS */}
        {activeContent === 'priorityDM' && <div className="col-span-12"><DirectMessages /></div>}
        {activeContent === 'notes' && <div className="col-span-12"><Notes /></div>}
        {activeContent === 'profile' && <div className="col-span-12"><ExpertProfile onProfileUpdate={toggleProfileModal} onCancel={toggleProfileModal} /></div>}
        {activeContent === 'availability' && <div className="col-span-12"><Availability /></div>}
        {activeContent === 'calendar' && (
          <div className="col-span-12">
            <div className="w-full overflow-x-auto">
              <DashboardCalendar
                bookingVersion={bookingVersion}
                onBookingAdded={() => setBookingVersion(v => v + 1)}
              />
            </div>
          </div>
        )}
        {activeContent === 'bookings' && <div className="col-span-12"><ExpertBookings /></div>}
        {activeContent === 'testimonials' && <div className="col-span-12"><ExpertTestimonials /></div>}
        {activeContent === 'services' && <div className="col-span-12"><ExpertServices /></div>}
        {activeContent === 'analytics' && <div className="col-span-12"><ExpertAnalytics /></div>}
        {activeContent === 'marketing' && <div className="col-span-12"><ExpertMarketing /></div>}
        {activeContent === 'payments' && <div className="col-span-12"><ExpertPayments /></div>}
        {activeContent === 'checkout' && <div className="col-span-12"><ExpertCheckout /></div>}
        {activeContent === 'achievements' && <div className="col-span-12"><ExpertAchievements /></div>}
        {activeContent === 'resources' && <div className="col-span-12"><ExpertResources /></div>}
      </main>

      {/* Profile modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl mt-16 shadow-lg overflow-auto">
            <ExpertProfile onProfileUpdate={toggleProfileModal} onCancel={toggleProfileModal} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TestDashboard;

/* ---------- MOBILE SEARCH COMPONENT ---------- */
function MobileSearch() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sm:hidden relative">
      {!open ? (
        <button className="p-2 rounded-lg bg-white shadow-sm" onClick={() => setOpen(true)}>
          <SearchIcon className="w-5 h-5 text-gray-600" />
        </button>
      ) : (
        <div className="absolute right-0 top-0 z-50">
          <div className="flex items-center gap-2 bg-white rounded-md shadow px-2 py-1">
            <input autoFocus type="text" placeholder="Search..." className="outline-none px-2" />
            <button className="p-1 text-gray-500" onClick={() => setOpen(false)}>✖</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- StatCard ---------- */
function StatCard({ value, label, icon }) {
  return (
    <Card className="rounded-xl shadow-sm">
      <CardContent className="pt-4 flex items-center space-x-3">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-400">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
